//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { useGetClientRetailersQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { CampaignRequestCurrencyField } from '@campaigns/components/campaigns-overview/CampaignRequestCurrencyField';
import { CampaignRequestDeliveryField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestDeliveryField';
import { CampaignRequestLocaleField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestLocaleField';
import { CampaignRequestNameField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestNameField';
import { CampaignRequestRetailerField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestRetailerField';
import { CampaignRequestRadioField } from '@campaigns/components/shared/CampaignRequestRadioField';
import {
  getCouponCodeProviderOptions,
  getShippingManagerOptions,
} from '@campaigns/constants/sampling/SamplingCreateCampaignModalConstants';
import { getIsSamplingTargetedRetailerEnabled } from '@campaigns/redux/selectors/getIsSamplingCampaignsTargetedRetailersEnabled';
import { getIsSamplingContentDestinationsEnabled } from '@campaigns/redux/selectors/getIsSamplingContentDestinationsEnabled';
import { isSingleRetailerPurchaseOption } from '@campaigns/utils/sampling/PrepaidCampaignUtils';
import { transformRetailersToDropdownOptions } from '@campaigns/utils/sampling/clientRetailerUtils';
import { getClientName } from '@redux/selectors/getSessionInfo';
import { isTruthy } from '@utils/filterPredicates';

interface CampaignModalContentSetupProps {
  shouldShowNameField?: boolean;
}

export const CampaignModalContentSetup = ({
  shouldShowNameField = false,
}: CampaignModalContentSetupProps) => {
  // TODO: PD-288921 remove this once the feature is ready
  const isSamplingTargetedRetailerEnabled = useSelector(
    getIsSamplingTargetedRetailerEnabled,
  );
  // TODO: PD-312744 remove this once the feature is ready
  const isSamplingContentDestinationsEnabled = useSelector(
    getIsSamplingContentDestinationsEnabled,
  );
  const { getValues, setValue, watch } =
    useFormContext<SamplingCampaignRequest>();
  const purchaseOptions = watch('purchaseOption');
  const isNewPurchase = watch('isNewPurchase');
  const clientName = useSelector(getClientName);
  const handleWhoShipsProductChange = (value: string) => {
    if (value === 'myCompany') {
      const currentValues = getValues();
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        boxType: undefined,
      });
    }
  };
  const handleCodeSupplierChange = (value: string) => {
    const currentValues = getValues();

    if (value === 'client') {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: [],
        clientCodesSupplied: false,
        codeScope: undefined,
        // If there are multiple retailers, keep only the first one since
        // we don't support multiple retailers for client-provided codes
        retailers:
          currentValues.fulfillmentOptions?.retailers?.slice(0, 1) ?? [],
      });
    } else {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: undefined,
        clientCodesSupplied: undefined,
        codeScope: undefined,
      });
    }
  };

  const { data: retailerOptions } = useGetClientRetailersQuery(
    transformRetailersToDropdownOptions,
  );

  const fulfillmentOptions = watch('fulfillmentOptions');

  const selectedPurchaseOption = purchaseOptions?.[0];

  const shouldHideRetailerCheckbox =
    (retailerOptions?.some((retailer) => retailer.label === clientName) ??
      false) ||
    // Remove isSamplingContentDestinationsEnabled check once content destination targeting is supported for prepaid purchases
    (isSamplingContentDestinationsEnabled &&
      selectedPurchaseOption !== undefined &&
      !isSingleRetailerPurchaseOption(selectedPurchaseOption));

  const fulfillmentOptionsList = selectedPurchaseOption?.fulfillmentOptions;

  const getAvailableCountryCodesFromFulfillment = () => {
    return Array.from(
      new Set(
        fulfillmentOptionsList?.flatMap(
          (option) => option.availableCountryCodes ?? [],
        ) ?? [],
      ),
    );
  };

  // show the delivery field when
  // = always for new purchases
  // = for existing purchases when fulfillmentType is not set
  const shouldShowDeliveryField =
    !fulfillmentOptions?.fulfillmentType || isNewPurchase;

  // show the shipping manage fields when
  // = always for new purchases if fulfillmentType is shipped
  // = for existing purchases when fulfillment type is 'shipped' and whoShipsProduct is not set
  const shouldShowShippingManagerField =
    fulfillmentOptions?.fulfillmentType === 'shipped' &&
    (isNewPurchase || !fulfillmentOptions?.whoShipsProduct);

  return (
    <>
      {shouldShowNameField && (
        <Container>
          <CampaignRequestNameField />
        </Container>
      )}
      <RowFields>
        <CampaignRequestLocaleField
          availableCountryCodes={getAvailableCountryCodesFromFulfillment()}
        />
        <CampaignRequestCurrencyField />
      </RowFields>
      {shouldShowDeliveryField && <CampaignRequestDeliveryField />}
      {shouldShowShippingManagerField && (
        <CampaignRequestRadioField
          label='Shipping manager'
          labelHelpText='Select whether you want Bazaarvoice to manage the shipping of products in the campaign, or to manage it yourself.'
          name='whoShipsProduct'
          onChange={handleWhoShipsProductChange}
          options={getShippingManagerOptions(clientName)}
        />
      )}
      {fulfillmentOptions?.fulfillmentType === 'deals' && (
        <CampaignRequestRadioField
          label='Coupon code provider'
          labelHelpText='Select whether you want Bazaarvoice to manage the coupon codes in the campaign, or to manage it yourself.'
          name='codeSupplier'
          onChange={handleCodeSupplierChange}
          options={getCouponCodeProviderOptions(clientName)}
        />
      )}
      {isSamplingTargetedRetailerEnabled &&
        isTruthy(retailerOptions?.length) &&
        !shouldHideRetailerCheckbox && (
          <CampaignRequestRetailerField retailerOptions={retailerOptions} />
        )}
    </>
  );
};

const Container = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[4]};
`,
);

const RowFields = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  gap: ${theme.spacings[3]};
`,
);
